import { useState } from 'react';
import { Plus, Pencil, Trash2, Send, Mail, MessageSquare, Bell, CheckCircle, Eye, Megaphone } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { API } from './constants';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const statusColors = {
  draft: 'bg-stone-100 text-stone-600',
  scheduled: 'bg-blue-100 text-blue-700',
  sent: 'bg-emerald-100 text-emerald-700'
};

const statusLabels = {
  draft: 'Borrador',
  scheduled: 'Programada',
  sent: 'Enviada'
};

export default function CampaignsTab({ campaigns, companies, detailedUsers, onRefresh }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm] = useState({
    name: '', message: '', channels: [], target_type: 'all', target_companies: [], target_users: []
  });

  const resetForm = () => {
    setForm({
      name: '', message: '', channels: [], target_type: 'all', target_companies: [], target_users: []
    });
    setEditingCampaign(null);
  };

  const toggleChannel = (channel) => {
    setForm(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  const toggleCompanyTarget = (companyName) => {
    setForm(prev => ({
      ...prev,
      target_companies: prev.target_companies.includes(companyName)
        ? prev.target_companies.filter(c => c !== companyName)
        : [...prev.target_companies, companyName]
    }));
  };

  const toggleUserTarget = (userId) => {
    setForm(prev => ({
      ...prev,
      target_users: prev.target_users.includes(userId)
        ? prev.target_users.filter(u => u !== userId)
        : [...prev.target_users, userId]
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.message || form.channels.length === 0) {
      toast.error('Completa nombre, mensaje y selecciona al menos un canal');
      return;
    }

    try {
      const url = editingCampaign 
        ? `${API}/admin/campaigns/${editingCampaign.campaign_id}`
        : `${API}/admin/campaigns`;
      
      const response = await fetch(url, {
        method: editingCampaign ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(editingCampaign ? 'Campaña actualizada' : 'Campaña creada');
        setDialogOpen(false);
        resetForm();
        onRefresh?.();
      } else {
        toast.error('Error al guardar campaña');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  const handleSend = async (campaignId) => {
    if (!window.confirm('¿Enviar esta campaña ahora?')) return;

    try {
      const response = await fetch(`${API}/admin/campaigns/${campaignId}/send`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message || 'Campaña enviada');
        onRefresh?.();
      } else {
        toast.error('Error al enviar campaña');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  const handleDelete = async (campaignId) => {
    if (!window.confirm('¿Eliminar esta campaña?')) return;
    
    try {
      const response = await fetch(`${API}/admin/campaigns/${campaignId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Campaña eliminada');
        onRefresh?.();
      }
    } catch (err) {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-stone-900">Campañas Promocionales</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              className="bg-emerald-700 hover:bg-emerald-800 rounded-full"
              onClick={resetForm}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Campaña
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCampaign ? 'Editar Campaña' : 'Nueva Campaña'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div>
                <label className="text-sm font-medium text-stone-700">Nombre de la Campaña</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="Ej: Promoción de Verano"
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700">Mensaje</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({...form, message: e.target.value})}
                  placeholder="Escribe el mensaje de tu campaña..."
                  className="mt-1 min-h-[100px]"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 block mb-3">Canales de Envío</label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { key: 'email', icon: Mail, label: 'Email' },
                    { key: 'whatsapp', icon: MessageSquare, label: 'WhatsApp' },
                    { key: 'in_app', icon: Bell, label: 'In-App' }
                  ].map(({ key, icon: Icon, label }) => (
                    <div
                      key={key}
                      onClick={() => toggleChannel(key)}
                      className={`cursor-pointer p-4 rounded-xl border-2 transition-all ${
                        form.channels.includes(key)
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-stone-200 hover:border-stone-300'
                      }`}
                    >
                      <Icon className={`w-6 h-6 mx-auto mb-2 ${form.channels.includes(key) ? 'text-emerald-600' : 'text-stone-400'}`} />
                      <p className={`text-sm font-medium text-center ${form.channels.includes(key) ? 'text-emerald-700' : 'text-stone-600'}`}>{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-stone-700 block mb-3">Audiencia Objetivo</label>
                <Select
                  value={form.target_type}
                  onValueChange={(v) => setForm({...form, target_type: v, target_companies: [], target_users: []})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los usuarios</SelectItem>
                    <SelectItem value="company">Por empresa</SelectItem>
                    <SelectItem value="custom">Selección personalizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.target_type === 'company' && (
                <div>
                  <label className="text-sm font-medium text-stone-700 block mb-3">Seleccionar Empresas</label>
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                    {companies.map((company) => (
                      <div
                        key={company.name}
                        onClick={() => toggleCompanyTarget(company.name)}
                        className={`cursor-pointer p-3 rounded-lg border transition-all flex items-center gap-2 ${
                          form.target_companies.includes(company.name)
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          form.target_companies.includes(company.name)
                            ? 'bg-emerald-500 border-emerald-500'
                            : 'border-stone-300'
                        }`}>
                          {form.target_companies.includes(company.name) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span className="text-sm text-stone-700">{company.name}</span>
                        <span className="text-xs text-stone-400">({company.users})</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {form.target_type === 'custom' && (
                <div>
                  <label className="text-sm font-medium text-stone-700 block mb-3">Seleccionar Usuarios</label>
                  <div className="max-h-60 overflow-y-auto border rounded-lg">
                    {detailedUsers.map((u) => (
                      <div
                        key={u.user_id}
                        onClick={() => toggleUserTarget(u.user_id)}
                        className={`cursor-pointer p-3 border-b last:border-b-0 flex items-center gap-3 transition-all ${
                          form.target_users.includes(u.user_id)
                            ? 'bg-emerald-50'
                            : 'hover:bg-stone-50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                          form.target_users.includes(u.user_id)
                            ? 'bg-emerald-500 border-emerald-500'
                            : 'border-stone-300'
                        }`}>
                          {form.target_users.includes(u.user_id) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-stone-900">{u.name}</p>
                          <p className="text-xs text-stone-500">{u.company} · {u.email}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-stone-500 mt-2">
                    {form.target_users.length} usuarios seleccionados
                  </p>
                </div>
              )}

              <Button 
                className="w-full bg-emerald-700 hover:bg-emerald-800"
                onClick={handleSave}
              >
                {editingCampaign ? 'Guardar Cambios' : 'Crear Campaña'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Campaign Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl p-6 border border-stone-200">
          <p className="text-sm text-stone-500">Total Campañas</p>
          <p className="text-2xl font-bold text-stone-900">{campaigns.length}</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-stone-200">
          <p className="text-sm text-stone-500">Enviadas</p>
          <p className="text-2xl font-bold text-emerald-700">{campaigns.filter(c => c.status === 'sent').length}</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-stone-200">
          <p className="text-sm text-stone-500">Borradores</p>
          <p className="text-2xl font-bold text-stone-600">{campaigns.filter(c => c.status === 'draft').length}</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-stone-200">
          <p className="text-sm text-stone-500">Usuarios Alcanzados</p>
          <p className="text-2xl font-bold text-blue-600">
            {campaigns.reduce((acc, c) => acc + (c.stats?.total_recipients || 0), 0)}
          </p>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Campaña</th>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Canales</th>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Audiencia</th>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Estado</th>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Fecha</th>
                <th className="text-left p-4 text-sm font-medium text-stone-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {campaigns.map(campaign => (
                <tr key={campaign.campaign_id} className="hover:bg-stone-50">
                  <td className="p-4">
                    <div>
                      <p className="font-medium text-stone-900">{campaign.name}</p>
                      <p className="text-sm text-stone-500 line-clamp-1">{campaign.message}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      {campaign.channels?.includes('email') && <Mail className="w-4 h-4 text-blue-500" />}
                      {campaign.channels?.includes('whatsapp') && <MessageSquare className="w-4 h-4 text-green-500" />}
                      {campaign.channels?.includes('in_app') && <Bell className="w-4 h-4 text-purple-500" />}
                    </div>
                  </td>
                  <td className="p-4 text-sm text-stone-600">
                    {campaign.target_type === 'all' ? 'Todos' :
                     campaign.target_type === 'company' ? `${campaign.target_companies?.length || 0} empresas` :
                     `${campaign.target_users?.length || 0} usuarios`}
                  </td>
                  <td className="p-4">
                    <Badge className={statusColors[campaign.status]}>
                      {statusLabels[campaign.status]}
                    </Badge>
                  </td>
                  <td className="p-4 text-sm text-stone-500">
                    {campaign.sent_at 
                      ? format(new Date(campaign.sent_at), 'dd MMM HH:mm', { locale: es })
                      : campaign.created_at 
                        ? format(new Date(campaign.created_at), 'dd MMM', { locale: es })
                        : '-'}
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      {campaign.status === 'draft' && (
                        <Button variant="ghost" size="sm" onClick={() => handleSend(campaign.campaign_id)}>
                          <Send className="w-4 h-4 text-emerald-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(campaign.campaign_id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {campaigns.length === 0 && (
          <div className="text-center py-12 text-stone-500">
            <Megaphone className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p>No hay campañas creadas</p>
          </div>
        )}
      </div>
    </div>
  );
}
